import { Store } from "@betomorrow/micro-stores"
import { Service } from "@modules/core/services/serviceType"
import { ProgramInfinite, ProgramInfiniteDraft } from "@modules/programs/programInfiniteTypes"
import { ProgramFilter, ProgramPrice, ProgramSubscriptionStatus } from "@modules/programs/programTypes"
import { ProgramApiGateway } from "@modules/programs/services/programApi.gateway"
import { ProgramInfiniteApi } from "@modules/programs/services/programInfiniteApi"
import { ProgramInfiniteDto, convertProgramInfiniteFromDto } from "@modules/programs/services/programInfiniteDto"
import { PlanningService } from "@modules/training/services/planningService"
import { ListOrder } from "@modules/utils/types"

export class ProgramInfiniteService implements Service {
  programInfiniteStore = new Store<ProgramInfinite>((id) => this.api.getProgramInfinite(id))

  constructor(
    private api: ProgramInfiniteApi,
    private planningService: PlanningService,
    private programApi: ProgramApiGateway,
  ) {}

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  async init(): Promise<void> {}

  async onLoggedOut(): Promise<void> {
    this.programInfiniteStore.clear()
  }

  async getProgramInfinite(programId: string) {
    return this.api.getProgramInfinite(programId)
  }

  async createProgramInfinite(programDraft: ProgramInfiniteDraft) {
    const program = await this.api.createProgramInfinite(programDraft)
    this.programInfiniteStore.save(program)
    return program
  }

  async updateProgramInfinite(id: string, programDraft: Partial<ProgramInfiniteDraft>) {
    const updatedProgram = await this.api.updateProgramInfinite(id, programDraft)
    this.programInfiniteStore.update(id, () => updatedProgram)
    return updatedProgram
  }

  async deleteProgramInfinite(id: string) {
    await this.api.deleteProgramInfinite(id)
    this.programInfiniteStore.remove(id)
  }

  async getInfinitePrograms(order: ListOrder, filter: ProgramFilter = "ALL", page?: number, size?: number) {
    return this.api.getInfinitePrograms(filter, order, page, size)
  }

  async updateProgramInfinitePrice(programId: string, price: ProgramPrice, trialDuration?: number) {
    const programDto = await this.programApi.updateProgramPrice(programId, price, trialDuration)
    this.programInfiniteStore.update(programId, () => convertProgramInfiniteFromDto(programDto as ProgramInfiniteDto))
  }

  async removeProgramInfinitePrice(programId: string) {
    const programDto = await this.programApi.removeProgramPrice(programId)
    this.programInfiniteStore.update(programId, () => convertProgramInfiniteFromDto(programDto as ProgramInfiniteDto))
  }

  async subscribeProgramInfinite(id: string): Promise<ProgramSubscriptionStatus> {
    const status = await this.api.subscribeProgramInfinite(id)

    this.programInfiniteStore.update(id, (program) => {
      return {
        ...program,
        subscriptionStatus: status,
      }
    })

    this.planningService.addNewFilteredProgram(id)

    return status
  }

  async unsubscribeProgramInfinite(id: string): Promise<ProgramSubscriptionStatus> {
    const status = await this.api.unsubscribeProgramInfinite(id)

    this.programInfiniteStore.update(id, (program) => {
      return {
        ...program,
        subscriptionStatus: status,
      }
    })

    this.planningService.removeFilteredProgram(id)

    return status
  }
}
